<script lang="ts">
    import { derived as sderived, writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import type { DateTime } from 'luxon'

    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'

    import 'codemirror/mode/javascript/javascript'

    import { sanchor, spath } from 'hydria/svelte_helpers'

    import CodeMirror from 'admin/components/CodeMirror.svelte'

    interface Props {
        route: any
    }

    let { route = {} }: Props = $props()

    let loading = $state(true)

    interface TaskLogEntry {
        stamp: DateTime
        entry: string
        entry_args: { [key: string]: any }
        new_state: 'READY' | 'RUNNING' | 'SUCCESS' | 'FAILED'
    }
    interface TaskComLogEntry {
        stamp: DateTime
        type: 'REQUEST' | 'RESPONSE'
        method: string
        data: any
    }
    interface Task {
        id: string
        user_id: string
        ctime: DateTime
        etime: DateTime
        rq_id: string
        initiated_by: string

        name: string
        name_args: { [key: string]: string }

        state: 'READY' | 'RUNNING' | 'SUCCESS' | 'FAILED'
        hidden: boolean

        step: number
        total_steps: number

        log: TaskLogEntry[]
        communication_log: TaskComLogEntry[]

        request_type: string
        request_params: { [key: string]: any }

        tags: string[]
    }

    let task: Task | undefined = $state(undefined)
    let taskId: Writable<string | null> = writable(null)

    let relaunching = $state(false)
    let hiding = $state(false)
    let deleting = $state(false)
    let deleted = $state(false)
    let preDelete = false

    async function loadTask(id: string) {
        loading = true
        task = undefined
        try {
            const r = await simpleFetch('GET', `/json/task/1/${id}`)
            task = r.data.item as Task
            for (const key of ['ctime', 'etime']) {
                if (task[key]) task[key] = toLuxon(task[key])
            }
            for (const entry of task.log) {
                if (entry['stamp']) entry['stamp'] = toLuxon(entry['stamp'])
            }
            for (const entry of task.communication_log) {
                if (entry['stamp']) entry['stamp'] = toLuxon(entry['stamp'])
            }
        } catch (e: any) {
            console.error('Failed to load task:', e)
            notify({
                type: 'danger',
                title: 'Task',
                text: 'La tâche n’a pas pu être chargée',
            })
        }

        loading = false
    }

    async function relaunch() {
        relaunching = true
        try {
            await simpleFetch('POST', `/json/task/1/${$taskId}/relaunch`)
            notify({
                type: 'success',
                title: 'Task',
                text: 'La tâche est relancée.',
            })
        } catch (e) {
            console.error('Failed to relaunch:', e)
            notify({
                type: 'danger',
                title: 'Task',
                text: 'La tâche n’a pas pu être relancée.',
            })
        }

        relaunching = false
    }
    async function set_hidden(newState: boolean) {
        if (!task) return

        hiding = true
        try {
            await simpleFetch(newState ? 'PUT' : 'DELETE', `/json/task/1/${$taskId}/hidden`)
            task.hidden = newState
            notify({
                type: 'success',
                title: 'Task',
                text: newState ? 'La tâche est cachée.' : 'La tâche est visible.',
            })
        } catch (e) {
            console.error('Failed to set hidden state:', e)
            notify({
                type: 'danger',
                title: 'Task',
                text: 'Le statut de la tâche n’a pas pu être modifié.',
            })
        }

        hiding = false
    }

    const crumbs = sderived(taskId, ($taskId) => [
        {
            txt: tgettext('Tasks'),
            url: spath('/tasks'),
        },
        {
            txt: $taskId,
        },
    ])
    $effect(() => {
        $taskId = route.result.path.params?.id
        loadTask(route.result.path.params?.id)
    })
</script>

<style>
    .command :global(.CodeMirror) {
        height: auto !important;
    }
</style>

<Breadcrumb {crumbs} />

<div class="card">
    <div class="card-title text-primary">{$_('Task Information')}</div>
    <div class="card-body">
        {#if loading}
            <div class="row">
                <div class="col-sm-6">
                    <dl class="row animated-loading">
                        <dt class="col-3">Name</dt>
                        <dd class="col-9"><div class="text-line" style="width: 75%"></div></dd>
                        <dt class="col-3">Current state</dt>
                        <dd class="col-9"><div class="text-line" style="width: 15%"></div></dd>
                        <dt class="col-3">Created</dt>
                        <dd class="col-9"><div class="text-line" style="width: 50%"></div></dd>
                        <dt class="col-3">Ended</dt>
                        <dd class="col-9"><div class="text-line" style="width: 50%"></div></dd>
                        <dt class="col-3">Owner</dt>
                        <dd class="col-9"><div class="text-line" style="width: 75%"></div></dd>
                    </dl>
                </div>
                <div class="col-sm-6">
                    <dl class="row animated-loading">
                        <dt class="col-3">Tags</dt>
                        <dd class="col-9"><div class="text-line" style="width: 25%"></div></dd>
                    </dl>
                </div>
            </div>
        {:else if task}
            <div class="row">
                <div class="col-sm-6">
                    <dl class="row">
                        <dt class="col-3">{$_('Name')}</dt>
                        <dd class="col-9">{$resolve(task.name, task.name_args)}</dd>
                        <dt class="col-3">{$_('Current state')}</dt>
                        <dd class="col-9">{task.state}</dd>
                        <dt class="col-3">{$_('Created')}</dt>
                        <dd class="col-9"><Date value={task.ctime} format="h-table-s" /></dd>
                        <dt class="col-3">{$_('Ended')}</dt>
                        <dd class="col-9"><Date value={task.etime} format="h-table-s" /></dd>
                        <dt class="col-3">{$_('Owner')}</dt>
                        <dd class="col-9"><a use:sanchor href={spath(`/wdom-user/${task.user_id}`)}>{task.user_id}</a></dd>
                        <dt class="col-3">{$_('Visibility')}</dt>
                        <dd class="col-9">
                            {#if hiding}
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Working…')}
                            {:else if task.hidden}
                                {$_('This task is hidden.')} <button type="button" class="btn btn-primary btn-sm" onclick={() => set_hidden(false)}>{$_('Unhide this task')}</button>
                            {:else}
                                {$_('This task ls visible.')} <button type="button" class="btn btn-primary btn-sm" onclick={() => set_hidden(true)}>{$_('Hide this task')}</button>
                            {/if}
                        </dd>
                    </dl>
                </div>
                <div class="col-sm-6">
                    <dl class="row">
                        <dt class="col-3">{$_('Tags')}</dt>
                        <dd class="col-9">
                            {#each task.tags as tag}
                                <span class="enum">{tag}</span>
                            {/each}
                        </dd>
                    </dl>
                    {#if task.state == 'FAILED'}
                        <div class="btn-toolbar float-end">
                            {#if relaunching}
                                <button type="button" class="btn btn-info btn-sm" disabled
                                    ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                    {$_('Relaunching…')}</button
                                >
                            {:else}
                                <button type="button" class="btn btn-danger btn-sm" disabled={preDelete || deleting || deleted} onclick={() => relaunch()}>{$_('Relaunch task')}</button>
                            {/if}
                            <!-- {#if deleted}
                                <button type="button" class="btn btn-info btn-sm" disabled>{$_('Task deleted')}</button>
                            {:else if deleting}
                                <button type="button" class="btn btn-info btn-sm" disabled><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> {$_('Deleting…')}</button>
                            {:else if !relaunching && !preDelete}
                                <button type="button" class="btn btn-warning btn-sm" on:click={() => (preDelete = true)}>{$_('Delete task')}</button>
                            {:else if preDelete}
                                <button type="button" class="btn btn-danger btn-sm" on:click={() => deleteTask()}>{$_('Confirm delete')}</button>
                                <button type="button" class="btn btn-success btn-sm" on:click={() => (preDelete = false)}>{$_('Abort delete')}</button>
                            {/if} -->
                        </div>
                    {/if}
                </div>
            </div>
        {:else}
            <p>Tâche inexistante ?</p>
        {/if}
    </div>
</div>

{#if !loading && task}
    <div class="card mt-4">
        <div class="card-title text-primary">{$_('Command')}</div>
        <div class="card-body command">
            <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: true, mode: { name: 'javascript', json: true } }} value={`${task.request_type}(${JSON.stringify(task.request_params)})`} />
        </div>
    </div>

    <div class="card mt-4">
        <div class="card-title text-primary">{$_('Log')}</div>
        <div class="card-body">
            <table class="table">
                <thead>
                    <tr>
                        <th class="col-sm-2">{$_('Timestamp')}</th>
                        <th class="col-sm-2">{$_('New state')}</th>
                        <th>{$_('Description')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each task.log as entry}
                        <tr>
                            <td><Date value={entry.stamp} format="h-table-s" /></td>
                            <td>{entry.new_state ? entry.new_state : '–'}</td>
                            <td>{$resolve(entry.entry, entry.entry_args)}</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    </div>

    <div class="card mt-4">
        <div class="card-title text-primary">{$_('Communication log')}</div>
        <div class="card-body">
            <table class="table">
                <thead>
                    <tr>
                        <th class="col-sm-2">{$_('Timestamp')}</th>
                        <th class="col-sm-1">{$_('Type')}</th>
                        <th class="col-sm-2">{$_('Method')}</th>
                        <th>{$_('Data')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each task.communication_log as entry}
                        <tr>
                            <td><Date value={entry.stamp} format="h-table-s" /></td>
                            <td><span class="tt-below" data-tooltip={entry.type}>{entry.type == 'REQUEST' ? '→' : '←'}</span></td>
                            <td>{entry.method}</td>
                            <td>
                                <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: true, mode: { name: 'javascript', json: true } }} value={JSON.stringify(entry.data, null, 2)} />
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    </div>
{/if}
